
import CanvasTextUtility from '@/classes/common/CanvasTextUtility';
import UserWindowSizeUtility from '@/classes/common/UserWindowSizeUtility';

/** カラー */
const BG_COLOR    = 'rgba(255, 255, 255, 1)';
const FOCUS_COLOR = 'rgba(192, 255, 192, 1)';
const FRAME_COLOR = 'rgba(160, 160, 160, 1)';
const RETRY_COLOR = 'rgba(255,  64,  64, 1)';
const PEN_COLOR   = 'rgba(0, 0, 0, 1)';

/** デフォルト1文字サイズ */
const DEFAULT_REPLY_SIZE_WIDTH  = 20;
const DEFAULT_REPLY_SIZE_HEIGHT = 20;

/** デフォルト1文字サイズ（Laege） */
const DEFAULT_REPLY_SIZE_WIDTH_LARGE  = 24;
const DEFAULT_REPLY_SIZE_HEIGHT_LARGE = 24;



/**
 * 回答欄管理クラス
 */
export default class JapaneseReplyView {

  // ------------------------------------------------------------------
  // - private field
  // ------------------------------------------------------------------

  /** 補正後コンポーネントサイズ */
  private _width  = 0;
  private _height = 0;

  /** WorkComponentからの相対左上x座標 */
  private _relativeLeftX = 0;

  /** WorkComponentからの相対左上y座標 */
  private _relativeTopY = 0;

  /** WorkComponentからの相対座標決定フラグ */
  private _isPositionDecided = false;

  /** 正解 */
  private _right = '';

  /** 文字数 */
  private _count = 0;

  /** 入力文字列 */
  private _text = '';

  /** フォーカスフラグ */
  private _focusFlag = false;

  /** 解き直しフラグ */
  private _retryFlag = false;

  /** 正解表示フラグ */
  private _rightFlag = false;

  /** フォントサイズフラグ */
  private _fontsizeSmallFlag = true;

  /** キャンバスコンテキスト */
  private _canvasContext: CanvasRenderingContext2D;




  // ------------------------------------------------------------------
  // - accessor
  // ------------------------------------------------------------------

  /** キャンバス幅を取得します */
  get width(): number {
    return this._width;
  }

  /** キャンバス高さを取得します */
  get height(): number {
    return this._height;
  }




  // ------------------------------------------------------------------
  // - constructor
  // ------------------------------------------------------------------

  /**
     * constructor
     *
     * @param canvasContext
     * @param count 文字数
     * @param fontsizeSmallFlag フォントサイズ
     */
  constructor(
    canvasContext: CanvasRenderingContext2D,
    count: number,
    fontsizeSmallFlag: boolean
  ) {
    this._canvasContext     = canvasContext;
    this._count             = count;
    this._fontsizeSmallFlag = fontsizeSmallFlag;

    this.setViewAndFontsize();
  }




  // ------------------------------------------------------------------
  // - public method
  // ------------------------------------------------------------------

  /**
     * WorkComponentからの相対座標をセットします。
     *
     * @param text 回答
     */
  public setCoordinate(x: number, y: number): void {
    this._relativeLeftX = x;
    this._relativeTopY = y;
    this._isPositionDecided = true;
  }

  /**
     * 回答をセットします。
     *
     * @param text 回答
     */
  public setText(text: string): void {
    if (text.length > this._right.length) text = text.substring(0, this._right.length);
    this._text = text;
    this.onDraw();
  }

  /**
     * 回答を取得します。
     *
     * @param 回答
     */
  public getText(): string {
    return this._text;
  }

  /**
     * 正解をセットします。
     *
     * @param right 正解
     */
  public setRight(right: string): void {
    this._right = right;
  }

  /**
     * 正解を取得します。
     *
     * @param 正解
     */
  public getRight(): string {
    return this._right;
  }

  /**
     * フォントサイズを変更します。
     *
     * @param fontsizeSmallFlag
     */
  public changeFontsize(fontsizeSmallFlag: boolean): void {
    this._fontsizeSmallFlag = fontsizeSmallFlag;

    this.setViewAndFontsize();

    this.onDraw();
  }

  /**
     * 判定を行います。
     *
     * @return 正解の場合は true を返却します。
     */
  public judge(): boolean {

    // 見分けがつかない「カタカナ」を「ひらがな」に寄せる
    const right = this.convertKana(this._right);
    const text  = this.convertKana(this._text).trim();

    this._retryFlag = right !== text;
    this.onDraw();

    return !this._retryFlag;
  }

  /**
     * 正解を表示します。
     */
  public dispRight(): void {
    this._rightFlag = true;
    this.onDraw();
  }

  /**
     * フォーカスフラグをセットします。
     *
     * @param focusFlag フォーカスフラグ
     */
  public setFocusFlag(focusFlag: boolean): void {
    this._focusFlag = focusFlag;
    this.onDraw();
  }

  /**
     * 座標がビュー内かどうか調べます
     *
     * @param x X座標データ
     * @param y Y座標データ
     * @return ビュー内の場合はtrueを返却します。
     */
  public hitTest(x: number, y: number): boolean {
    if (x < this._relativeLeftX || this._relativeLeftX + this.width < x) return false;
    if (y < this._relativeTopY  || this._relativeTopY + this.height < y) return false;
    return true;
  }

  /**
     * 描画処理
     */
  public onDraw(): void {
    // 座標確定前は描画しない
    if (!this._isPositionDecided) return;

    // 背景の描画
    if (this._focusFlag) this._canvasContext.fillStyle = FOCUS_COLOR;
    else                 this._canvasContext.fillStyle = BG_COLOR;
    this._canvasContext.fillRect(this._relativeLeftX, this._relativeTopY, this.width, this.height);

    // 外枠の描画
    if (this._retryFlag) this._canvasContext.strokeStyle = RETRY_COLOR;
    else                 this._canvasContext.strokeStyle = FRAME_COLOR;
    this._canvasContext.lineWidth = 2;

    let y = this._relativeTopY;

    // 読解の場合は1文字ごとに枠で囲む
    const penSizeS = UserWindowSizeUtility.penSizeS();
    for (let a = 0; a < this._count; a++) {
      this._canvasContext.strokeRect(this._relativeLeftX, y, this.width, this.width);
      y += this.width - penSizeS;
    }

    // 文字の描画
    y = this._relativeTopY;
    let r8  = this._relativeLeftX + UserWindowSizeUtility.calculateRatio(4);
    let r28 = UserWindowSizeUtility.calculateRatio(14);

    if (!this._fontsizeSmallFlag) {
      r8  = this._relativeLeftX + UserWindowSizeUtility.calculateRatio(3);
      r28 = UserWindowSizeUtility.calculateRatio(19);
    }

    let text = this._text;
    this._canvasContext.fillStyle = PEN_COLOR;
    if (this._rightFlag && this._retryFlag) {
      text = this._right;
      this._canvasContext.fillStyle = RETRY_COLOR;
    }

    if (this._fontsizeSmallFlag) {
      this._canvasContext.font = UserWindowSizeUtility.canvasFont(UserWindowSizeUtility.fontSize(), false);
    } else {
      this._canvasContext.font = UserWindowSizeUtility.canvasFont(UserWindowSizeUtility.fontSize() * 3 / 2, false);
    }

    for (let i = 0; i < text.length; i++) {
      // 文字の縦描画
      CanvasTextUtility.drawVertical(this._canvasContext, text.charAt(i), r8, y + r28);
      y += this.width - penSizeS;
    }

    // 描画が終了したら手書きの線太、線色に戻しておく
    this._canvasContext.lineWidth = UserWindowSizeUtility.penSize();
    this._canvasContext.strokeStyle = PEN_COLOR;
  }




  // ------------------------------------------------------------------
  // - private method
  // ------------------------------------------------------------------

  /**
     * サイズをセットします。
     */
  private setViewAndFontsize(): void {

    let dx = UserWindowSizeUtility.calculateRatio(DEFAULT_REPLY_SIZE_WIDTH);
    let dy = UserWindowSizeUtility.calculateRatio(DEFAULT_REPLY_SIZE_HEIGHT);

    if (!this._fontsizeSmallFlag) {
      dx = UserWindowSizeUtility.calculateRatio(DEFAULT_REPLY_SIZE_WIDTH_LARGE);
      dy = UserWindowSizeUtility.calculateRatio(DEFAULT_REPLY_SIZE_HEIGHT_LARGE);
    }

    this._width  = dx;
    this._height = dy * this._count - UserWindowSizeUtility.penSizeS() * (this._count - 1);

    // Androidはここでフォントをセットしたけど、効かないので onDraw に移動
  }

  /**
     * カタカナの「ヘ」「ベ」「ペ」「リ」をひらがなの「へ」「べ」「ぺ」「り」に変換します。
     * 漢字の「口」をカタカナの「ロ」に変換します。
     *
     * @param 変換前文字列
     * @return 変換後文字列
     */
  private convertKana(str: string): string {
    str = str.replace('ヘ', 'へ');
    str = str.replace('ベ', 'べ');
    str = str.replace('ペ', 'ぺ');
    str = str.replace('リ', 'り');
    str = str.replace('口', 'ロ');
    return str;
  }




}
